import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CalendarBlank, User as UserIcon } from "@phosphor-icons/react";
import MarketingLayout from "@/components/MarketingLayout";
import SEO from "@/components/SEO";
import { api } from "@/lib/api";

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); }
  catch { return iso; }
}

// Tiny markdown-ish formatter: headings (#), paragraphs, lists (- item), bold (**text**)
// Plus rich block fences: :::divider, :::callout amber|blue, :::path, :::features, :::quote, :::cta, :::tags
function renderBody(body) {
  if (!body) return null;
  const inlineFmt = (s) =>
    s.replace(/\*\*(.+?)\*\*/g, '<strong class="text-stoneink-900">$1</strong>')
     .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" class="text-terracotta-600 hover:underline">$1</a>');

  // 1) First, split into top-level blocks: text segments and ::: fenced rich blocks
  const fenceRe = /:::([a-z]+)(?:[ \t]+([a-z]+))?\n([\s\S]*?)\n:::/g;
  const segments = [];
  let lastIdx = 0;
  let m;
  while ((m = fenceRe.exec(body)) !== null) {
    if (m.index > lastIdx) segments.push({ type: "text", value: body.slice(lastIdx, m.index) });
    segments.push({ type: m[1], variant: m[2] || null, value: m[3] });
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < body.length) segments.push({ type: "text", value: body.slice(lastIdx) });

  const renderText = (text, keyBase) => {
    const lines = text.split(/\r?\n/);
    const out = [];
    let listBuffer = [];
    const flushList = () => {
      if (listBuffer.length) {
        out.push(
          <ul key={`${keyBase}-l-${out.length}`} className="list-disc pl-6 my-4 space-y-1 text-stoneink-700">
            {listBuffer.map((it, i) => <li key={i} dangerouslySetInnerHTML={{ __html: inlineFmt(it) }} />)}
          </ul>
        );
        listBuffer = [];
      }
    };
    lines.forEach((raw, i) => {
      const ln = raw.trimEnd();
      const k = `${keyBase}-${i}`;
      if (!ln.trim()) { flushList(); return; }
      if (ln.startsWith("### ")) { flushList(); out.push(<h3 key={k} className="font-heading text-xl text-stoneink-900 font-medium mt-7 mb-3" dangerouslySetInnerHTML={{ __html: inlineFmt(ln.slice(4)) }} />); return; }
      if (ln.startsWith("## ")) { flushList(); out.push(<h2 key={k} className="font-heading text-2xl sm:text-3xl text-stoneink-900 font-semibold tracking-tight mt-10 mb-4" dangerouslySetInnerHTML={{ __html: inlineFmt(ln.slice(3)) }} />); return; }
      if (ln.startsWith("# ")) { flushList(); out.push(<h1 key={k} className="font-heading text-3xl text-stoneink-900 font-semibold tracking-tight mt-8 mb-4" dangerouslySetInnerHTML={{ __html: inlineFmt(ln.slice(2)) }} />); return; }
      if (ln.startsWith("- ")) { listBuffer.push(ln.slice(2)); return; }
      flushList();
      out.push(<p key={k} className="text-stoneink-700 leading-relaxed my-4 text-[1.0625rem]" dangerouslySetInnerHTML={{ __html: inlineFmt(ln) }} />);
    });
    flushList();
    return out;
  };

  return segments.map((seg, idx) => {
    const k = `seg-${idx}`;
    if (seg.type === "text") return <div key={k}>{renderText(seg.value, k)}</div>;

    if (seg.type === "divider") {
      return <hr key={k} className="my-12 border-0 h-px bg-gradient-to-r from-transparent via-sand-300 to-transparent" />;
    }

    if (seg.type === "callout") {
      const isBlue = seg.variant === "blue";
      const palette = isBlue
        ? "bg-sage-400/10 border-sage-400/40 text-stoneink-700"
        : "bg-ochre-400/15 border-ochre-400/40 text-stoneink-700";
      const labelColor = isBlue ? "text-sage-600" : "text-ochre-600";
      // first line may be "Label: ..." -> use as label; otherwise no label
      const lines = seg.value.split(/\r?\n/);
      let label = null;
      let bodyLines = lines;
      const labelMatch = lines[0]?.match(/^\[(.+)\]\s*$/);
      if (labelMatch) { label = labelMatch[1]; bodyLines = lines.slice(1); }
      return (
        <aside key={k} className={`my-8 rounded-2xl border ${palette} px-6 py-5 shadow-ambient`}>
          {label && (
            <div className={`text-xs font-bold uppercase tracking-[0.18em] ${labelColor} mb-2`}>{label}</div>
          )}
          <div className="space-y-3">
            {bodyLines.filter(Boolean).map((l, i) => (
              <p key={i} className="text-stoneink-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: inlineFmt(l) }} />
            ))}
          </div>
        </aside>
      );
    }

    if (seg.type === "path") {
      // syntax: each line "Label::Value", final item gets accent
      const items = seg.value.split(/\r?\n/).map(l => l.trim()).filter(Boolean).map(l => {
        const [label, value] = l.split("::").map(s => s.trim());
        return { label, value };
      });
      return (
        <div key={k} className="my-10 rounded-2xl border border-sand-300 bg-sand-100 p-6 sm:p-7">
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
            {items.map((it, i) => (
              <div key={i} className="contents">
                <div className={`flex flex-col items-start gap-1 rounded-xl px-4 py-3 ${i === items.length - 1 ? "bg-terracotta-600 text-white" : "bg-white border border-sand-300"}`}>
                  <span className={`text-[10px] font-bold uppercase tracking-[0.18em] ${i === items.length - 1 ? "text-white/80" : "text-stoneink-500"}`}>{it.label}</span>
                  <span className={`font-heading text-lg font-medium ${i === items.length - 1 ? "text-white" : "text-stoneink-900"}`}>{it.value}</span>
                </div>
                {i < items.length - 1 && <span className="text-terracotta-600 text-2xl font-light">›</span>}
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (seg.type === "features") {
      // syntax per card: "ICON | Heading | body text..."  one card per line
      const cards = seg.value.split(/\r?\n/).map(l => l.trim()).filter(Boolean).map(l => {
        const parts = l.split("|").map(s => s.trim());
        return { icon: parts[0], title: parts[1], body: parts.slice(2).join(" | ") };
      });
      return (
        <div key={k} className="my-10 grid grid-cols-1 sm:grid-cols-2 gap-5">
          {cards.map((c, i) => (
            <div key={i} className="rounded-2xl border border-sand-300 bg-white p-6 shadow-ambient hover:shadow-hover hover:-translate-y-0.5 transition-all">
              <div className="h-11 w-11 rounded-xl bg-terracotta-50 grid place-items-center text-2xl mb-3">{c.icon}</div>
              <h3 className="font-heading text-lg text-stoneink-900 font-semibold mb-2">{c.title}</h3>
              <p className="text-stoneink-700 leading-relaxed text-sm" dangerouslySetInnerHTML={{ __html: inlineFmt(c.body) }} />
            </div>
          ))}
        </div>
      );
    }

    if (seg.type === "quote") {
      // optional last line "— cite"
      const lines = seg.value.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
      let cite = null;
      let quoteLines = lines;
      if (lines.length > 1 && lines[lines.length - 1].startsWith("—")) {
        cite = lines[lines.length - 1].replace(/^—\s*/, "");
        quoteLines = lines.slice(0, -1);
      }
      return (
        <figure key={k} className="my-10 border-l-4 border-terracotta-600 pl-6 py-2">
          <blockquote className="font-heading text-xl sm:text-2xl text-stoneink-900 leading-snug italic">
            {quoteLines.join(" ")}
          </blockquote>
          {cite && <figcaption className="mt-3 text-sm font-medium text-terracotta-600 not-italic">— {cite}</figcaption>}
        </figure>
      );
    }

    if (seg.type === "cta") {
      // syntax:
      // Heading::
      // Body text
      // primary::Button label::/url
      // ghost::Button label::/url
      const lines = seg.value.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
      const heading = lines[0]?.replace(/::\s*$/, "");
      const buttons = [];
      const bodyLines = [];
      lines.slice(1).forEach(l => {
        if (l.startsWith("primary::") || l.startsWith("ghost::")) {
          const [kind, label, url] = l.split("::").map(s => s.trim());
          buttons.push({ kind, label, url });
        } else {
          bodyLines.push(l);
        }
      });
      return (
        <div key={k} className="my-14 rounded-3xl bg-stoneink-900 text-white p-8 sm:p-10 text-center shadow-hover">
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">{heading}</h2>
          {bodyLines.length > 0 && (
            <p className="mt-3 text-sand-200 leading-relaxed max-w-2xl mx-auto" dangerouslySetInnerHTML={{ __html: inlineFmt(bodyLines.join(" ")) }} />
          )}
          {buttons.length > 0 && (
            <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
              {buttons.map((b, i) => (
                <a
                  key={i}
                  href={b.url}
                  data-testid={`blog-cta-${b.kind}`}
                  className={
                    b.kind === "primary"
                      ? "inline-flex items-center justify-center h-11 px-6 rounded-xl bg-terracotta-600 hover:bg-terracotta-700 text-white font-medium transition-colors"
                      : "inline-flex items-center justify-center h-11 px-6 rounded-xl border border-white/30 hover:border-white/60 hover:bg-white/5 text-white font-medium transition-colors"
                  }
                >
                  {b.label}
                </a>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (seg.type === "tags") {
      const tags = seg.value.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
      return (
        <div key={k} className="mt-12 pt-8 border-t border-sand-300 flex flex-wrap gap-2">
          {tags.map((t, i) => (
            <span key={i} className="inline-block px-3 py-1.5 rounded-full bg-sand-100 border border-sand-300 text-xs font-medium text-stoneink-700">
              {t}
            </span>
          ))}
        </div>
      );
    }

    return null;
  });
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/blog/posts/${slug}`).then(({ data }) => setPost(data)).catch(() => setErr("Post not found"));
  }, [slug]);

  if (err) return (
    <MarketingLayout>
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="blogpost-missing">
        <h1 className="font-heading text-3xl text-stoneink-900 font-semibold">We couldn&apos;t find that post.</h1>
        <p className="mt-3 text-stoneink-700">It may have moved or been unpublished.</p>
        <Link to="/blog" className="mt-6 inline-flex items-center gap-2 text-terracotta-600 hover:text-terracotta-700 font-medium">
          <ArrowLeft size={16} /> Back to the blog
        </Link>
      </div>
    </MarketingLayout>
  );
  if (!post) return (
    <MarketingLayout>
      <div className="max-w-3xl mx-auto px-6 py-24 text-stoneink-500">Loading…</div>
    </MarketingLayout>
  );

  return (
    <MarketingLayout>
      <SEO
        title={post.title}
        description={post.excerpt || post.title}
        path={`/blog/${post.slug}`}
        type="article"
        image={post.cover_image || undefined}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": post.title,
          "description": post.excerpt || post.title,
          "image": post.cover_image || "https://nestledapp.io/og-image.png",
          "datePublished": post.published_at,
          "dateModified": post.published_at,
          "author": { "@type": "Person", "name": post.author_name || "Nestled" },
          "publisher": {
            "@type": "Organization",
            "name": "Nestled",
            "logo": { "@type": "ImageObject", "url": "https://nestledapp.io/og-image.png" },
          },
          "mainEntityOfPage": { "@type": "WebPage", "@id": `https://nestledapp.io/blog/${post.slug}` },
        }}
      />
      <article className="max-w-3xl mx-auto px-6 md:px-10 pt-12 pb-24" data-testid="blogpost-article">
        <Link to="/blog" className="inline-flex items-center gap-2 text-stoneink-700 hover:text-terracotta-600 text-sm">
          <ArrowLeft size={16} /> All posts
        </Link>

        <div className="mt-8 flex items-center gap-4 text-sm text-stoneink-500">
          <span className="inline-flex items-center gap-1.5"><CalendarBlank size={14} /> {fmtDate(post.published_at)}</span>
          <span className="inline-flex items-center gap-1.5"><UserIcon size={14} /> {post.author_name}</span>
        </div>
        <motion.h1 initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="mt-3 font-heading text-4xl sm:text-5xl text-stoneink-900 font-semibold tracking-tight leading-[1.1]">{post.title}</motion.h1>
        {post.excerpt && <p className="mt-5 text-xl text-stoneink-700 leading-relaxed">{post.excerpt}</p>}

        {post.cover_image && (
          <div className="mt-10 aspect-[16/9] rounded-3xl overflow-hidden border border-sand-300">
            <img src={post.cover_image} alt="" className="w-full h-full object-cover" />
          </div>
        )}

        <div className="mt-10">{renderBody(post.body)}</div>
      </article>
    </MarketingLayout>
  );
}
